import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input", 
    "dropzone", 
    "uploadArea", 
    "dragOverlay", 
    "previewArea", 
    "filePreview",
    "progressArea",
    "progressBar",
    "progressText",
    "successMessage"
  ]

  static values = {
    accept: String,
    multiple: Boolean,
    maxSize: Number,
    preview: Boolean
  }

  connect() {
    this.dragCounter = 0
    this.setupEventListeners()
  }

  setupEventListeners() {
    // File input change
    this.inputTarget.addEventListener('change', this.handleFileSelect.bind(this))
    
    // Drag and drop events
    this.dropzoneTarget.addEventListener('dragenter', this.handleDragEnter.bind(this))
    this.dropzoneTarget.addEventListener('dragover', this.handleDragOver.bind(this))
    this.dropzoneTarget.addEventListener('dragleave', this.handleDragLeave.bind(this))
    this.dropzoneTarget.addEventListener('drop', this.handleDrop.bind(this))
    
    // Click to select file
    this.dropzoneTarget.addEventListener('click', this.handleClick.bind(this))
  }

  handleClick(event) {
    // Only trigger file selection if clicking on the dropzone itself or the upload area
    if (event.target === this.dropzoneTarget || this.uploadAreaTarget.contains(event.target)) {
      this.inputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  handleDragEnter(event) {
    event.preventDefault()
    this.dragCounter++
    this.showDragOverlay()
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dragCounter--
    if (this.dragCounter === 0) {
      this.hideDragOverlay()
    }
  }

  handleDrop(event) {
    event.preventDefault()
    this.dragCounter = 0
    this.hideDragOverlay()
    
    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.processFiles(files)
    }
  }

  processFiles(files) {
    // Reset any previous states
    this.hideMessages()
    
    // Convert FileList to Array
    const fileArray = Array.from(files)
    
    // Validate files
    const validationResult = this.validateFiles(fileArray)
    
    if (!validationResult.valid) {
      this.showError(validationResult.errors)
      return
    }

    // Show preview if enabled
    if (this.previewValue) {
      this.showFilePreview(fileArray)
    }

    // Dispatch custom event with file data
    this.dispatch('filesSelected', {
      detail: {
        files: fileArray,
        valid: true
      }
    })
  }

  validateFiles(files) {
    const errors = []
    
    // Check if multiple files are allowed
    if (!this.multipleValue && files.length > 1) {
      errors.push('Only one file is allowed')
    }

    files.forEach((file, index) => {
      // Check file type
      if (this.acceptValue && !this.isFileTypeAccepted(file)) {
        errors.push(`File ${index + 1}: Invalid file type. Expected: ${this.acceptValue}`)
      }

      // Check file size
      if (this.maxSizeValue && file.size > this.maxSizeValue) {
        const maxSizeMB = (this.maxSizeValue / (1024 * 1024)).toFixed(1)
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
        errors.push(`File ${index + 1}: File size (${fileSizeMB}MB) exceeds maximum (${maxSizeMB}MB)`)
      }
    })

    return {
      valid: errors.length === 0,
      errors: errors
    }
  }

  isFileTypeAccepted(file) {
    if (!this.acceptValue) return true
    
    const acceptedTypes = this.acceptValue.split(',').map(type => type.trim())
    const fileName = file.name.toLowerCase()
    const fileType = file.type.toLowerCase()

    return acceptedTypes.some(acceptedType => {
      if (acceptedType.startsWith('.')) {
        // Extension check
        return fileName.endsWith(acceptedType.toLowerCase())
      } else {
        // MIME type check
        return fileType === acceptedType.toLowerCase() || 
               fileType.startsWith(acceptedType.toLowerCase() + '/')
      }
    })
  }

  showFilePreview(files) {
    if (!this.hasPreviewAreaTarget) return

    const file = files[0] // Show preview for first file
    const fileSize = this.formatFileSize(file.size)
    const fileType = file.type || 'Unknown'
    const fileName = file.name
    const fileExtension = fileName.split('.').pop().toUpperCase()

    this.filePreviewTarget.innerHTML = `
      <div class="flex items-center space-x-4">
        <div class="flex-shrink-0">
          <div class="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center space-x-2">
            <p class="text-sm font-semibold text-gray-900 truncate">${fileName}</p>
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              ${fileExtension}
            </span>
          </div>
          <div class="flex items-center space-x-4 mt-1">
            <p class="text-sm text-gray-500">${fileSize}</p>
            <div class="flex items-center space-x-1">
              <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm text-green-600 font-medium">Ready to upload</span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0">
          <button type="button" class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200" data-action="click->file-upload#removeFile" title="Remove file">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    `

    this.previewAreaTarget.classList.remove('hidden')
  }

  removeFile() {
    this.inputTarget.value = ''
    this.previewAreaTarget.classList.add('hidden')
    this.hideMessages()
    
    this.dispatch('fileRemoved', {
      detail: { removed: true }
    })
  }

  showProgress(percentage = 0, text = 'Uploading...') {
    if (!this.hasProgressAreaTarget) return

    this.progressAreaTarget.classList.remove('hidden')
    this.progressBarTarget.style.width = `${percentage}%`
    this.progressTextTarget.textContent = text
  }

  hideProgress() {
    if (!this.hasProgressAreaTarget) return
    this.progressAreaTarget.classList.add('hidden')
  }

  showSuccess(message = 'Upload successful!') {
    if (!this.hasSuccessMessageTarget) return

    this.successMessageTarget.classList.remove('hidden')
    this.successMessageTarget.querySelector('p').textContent = message
  }

  showError(errors) {
    // Find error container in parent or create one
    const errorContainer = this.element.querySelector('.error-messages') || this.createErrorContainer()
    
    const totalErrors = errors.length
    const errorList = errors.map(error => `<li class="text-sm">${error}</li>`).join('')
    
    errorContainer.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-lg shadow-sm">
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold text-red-800">Upload Error</h3>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  ${totalErrors} error${totalErrors !== 1 ? 's' : ''}
                </span>
              </div>
              <div class="mt-2 text-sm text-red-700">
                <ul class="space-y-1">
                  ${errorList}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
    errorContainer.classList.remove('hidden')
  }

  createErrorContainer() {
    const errorContainer = document.createElement('div')
    errorContainer.className = 'error-messages mt-4 hidden'
    this.element.appendChild(errorContainer)
    return errorContainer
  }

  hideMessages() {
    // Hide all message containers
    const errorContainer = this.element.querySelector('.error-messages')
    if (errorContainer) {
      errorContainer.classList.add('hidden')
    }
    
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add('hidden')
    }
    
    this.hideProgress()
  }

  showDragOverlay() {
    if (this.hasDragOverlayTarget) {
      this.dragOverlayTarget.classList.remove('opacity-0', 'pointer-events-none')
      this.dragOverlayTarget.classList.add('opacity-100')
    }
  }

  hideDragOverlay() {
    if (this.hasDragOverlayTarget) {
      this.dragOverlayTarget.classList.add('opacity-0', 'pointer-events-none')
      this.dragOverlayTarget.classList.remove('opacity-100')
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Public methods for external use
  reset() {
    this.inputTarget.value = ''
    this.previewAreaTarget.classList.add('hidden')
    this.hideMessages()
    this.dragCounter = 0
    this.hideDragOverlay()
  }

  getFiles() {
    return this.inputTarget.files
  }

  hasFiles() {
    return this.inputTarget.files.length > 0
  }
} 